import type { BoardRange, LeaderboardEntry } from "../useLeaderboard.js";

const MEDAL = [
  "text-gold drop-shadow-[0_0_6px_rgba(255,212,94,0.45)]",
  "text-silver drop-shadow-[0_0_6px_rgba(205,210,220,0.35)]",
  "text-bronze drop-shadow-[0_0_6px_rgba(217,143,90,0.35)]",
] as const;

/**
 * Top-N board (server already orders it per the game's dir). Highlights the player's own
 * handle. `formatValue` renders the brag number with its unit (e.g. ms, day streak) — it
 * defaults to the bare number. Entertainment only; no anti-cheat (IDEA.md non-goals).
 *
 * Pass `range` + `onRange` to get the all-time | today tabs — "today" is the daily
 * race (resets at UTC midnight), an open podium even when all-time has calcified.
 */
export function Leaderboard({
  entries,
  loading,
  myHandle,
  formatValue = (v) => String(v),
  emptyLabel = "no scores yet — be the first.",
  range,
  onRange,
}: {
  entries: LeaderboardEntry[];
  loading: boolean;
  myHandle: string | null;
  formatValue?: (value: number) => string;
  emptyLabel?: string;
  range?: BoardRange;
  onRange?: (range: BoardRange) => void;
}) {
  const tabs =
    range !== undefined && onRange !== undefined ? (
      <div className="flex gap-1 rounded-full border border-border p-0.5 text-xs">
        {(["all", "today"] as const).map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => onRange(r)}
            className={`rounded-full px-3 py-1 font-semibold transition-colors ${
              range === r ? "bg-accent text-bg" : "text-text-muted hover:text-text"
            }`}
          >
            {r === "all" ? "all-time" : "today"}
          </button>
        ))}
      </div>
    ) : null;

  if (loading && entries.length === 0) {
    return (
      <>
        {tabs}
        <p className="text-sm text-text-faint">loading leaderboard…</p>
      </>
    );
  }
  if (entries.length === 0) {
    return (
      <>
        {tabs}
        <p className="text-sm text-text-faint">
          {range === "today" ? "nobody's on today's board yet — open podium." : emptyLabel}
        </p>
      </>
    );
  }
  return (
    <>
      {tabs}
    <ol className="w-full max-w-sm divide-y divide-border/60">
      {entries.map((e, i) => {
        const mine = myHandle !== null && e.handle === myHandle;
        return (
          <li
            key={`${e.handle}-${e.createdAt}-${i}`}
            className={`-mx-2 flex items-center justify-between rounded-md px-2 py-2 transition-colors hover:bg-surface-raised/60 ${
              mine ? "bg-accent/5 text-accent" : "text-text"
            }`}
          >
            <span className="flex items-center gap-3">
              <span
                className={`w-6 text-right font-mono text-sm font-bold ${
                  MEDAL[i] ?? "font-normal text-text-faint"
                }`}
              >
                {i + 1}
              </span>
              <span className="font-medium">{e.handle}</span>
              {mine && (
                <span className="rounded-full border border-accent/40 px-1.5 text-[10px] uppercase tracking-wide text-accent">
                  you
                </span>
              )}
            </span>
            <span
              className={`font-mono font-semibold ${i === 0 ? "text-gold" : ""}`}
            >
              {formatValue(e.value)}
            </span>
          </li>
        );
      })}
    </ol>
    </>
  );
}
